/**
 * OutboxSettingsModal — Jul-2026 (User-Wunsch, Zahnrad Dokumenten-Postausgang).
 * Je Dokumenttyp: E-Mail an den Kunden automatisch / vorher fragen / nie.
 * Plus Verwaltung der bestehenden Ignorier-Regeln (Dateinamen).
 */
import { useEffect, useState } from "react";
import { Loader2, Save, X, Ban, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";

const MODES = [
    ["auto", "Automatisch senden"],
    ["evening", "Abends gesammelt (1 E-Mail pro Kunde)"],
    ["ask", "Vorher fragen"],
    ["never", "Nie senden"],
];

export const OutboxSettingsModal = ({ open, onClose }) => {
    const [types, setTypes] = useState([]);
    const [ignoreRules, setIgnoreRules] = useState([]);
    const [newIgnore, setNewIgnore] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        Promise.all([
            api.get("/documents/outbox/settings"),
            api.get("/documents/outbox/ignore"),
        ]).then(([s, ig]) => {
            setTypes(s.data.types || []);
            setIgnoreRules(ig.data.items || []);
        }).catch(() => toast.error("Einstellungen konnten nicht geladen werden"))
            .finally(() => setLoading(false));
    }, [open]);

    const setMode = (key, mode) =>
        setTypes((t) => t.map((x) => (x.key === key ? { ...x, email_mode: mode } : x)));

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/documents/outbox/settings", {
                email_by_type: Object.fromEntries(types.map((t) => [t.key, t.email_mode])),
            });
            toast.success("Postausgang-Regeln gespeichert");
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setSaving(false);
        }
    };

    const addIgnore = async () => {
        const fn = newIgnore.trim();
        if (!fn) return;
        try {
            await api.post("/documents/outbox/ignore", { filename: fn });
            setIgnoreRules((r) => [{ id: `tmp-${fn}`, filename: fn }, ...r.filter((x) => x.filename !== fn)]);
            setNewIgnore("");
            toast.success(`„${fn}" wird künftig ignoriert`);
        } catch {
            toast.error("Regel konnte nicht angelegt werden");
        }
    };

    const removeIgnore = async (rule) => {
        try {
            await api.delete(`/documents/outbox/ignore/${rule.id}`);
            setIgnoreRules((r) => r.filter((x) => x.id !== rule.id));
        } catch {
            toast.error("Regel konnte nicht entfernt werden");
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="outbox-settings-dialog">
                <DialogHeader>
                    <DialogTitle>Dokumenten-Postausgang — Einstellungen</DialogTitle>
                </DialogHeader>
                {loading ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500 py-4 justify-center">
                        <Loader2 className="w-4 h-4 animate-spin" /> Lade…
                    </div>
                ) : (
                    <div className="space-y-5 py-1">
                        <div>
                            <h3 className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-2">
                                E-Mail an den Kunden je Dokumenttyp
                            </h3>
                            <ul className="space-y-1.5">
                                {types.map((t) => (
                                    <li key={t.key} className="flex items-center gap-2 border border-gray-200 rounded-sm px-2.5 py-1.5" data-testid={`outbox-type-${t.key}`}>
                                        <span className="text-sm font-bold text-gray-800 flex-1">{t.label}</span>
                                        <select value={t.email_mode} onChange={(e) => setMode(t.key, e.target.value)}
                                            className="h-8 px-2 rounded-sm border border-gray-300 text-xs bg-white"
                                            data-testid={`outbox-type-mode-${t.key}`}>
                                            {MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                        </select>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div>
                            <h3 className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-2 inline-flex items-center gap-1.5">
                                <Ban className="w-3.5 h-3.5" /> Ignorier-Regeln (Dateinamen)
                            </h3>
                            <div className="flex items-center gap-2 mb-2">
                                <input value={newIgnore} onChange={(e) => setNewIgnore(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addIgnore(); } }}
                                    placeholder="z. B. scan_deckblatt.pdf"
                                    className="flex-1 h-8 px-2 rounded-sm border border-gray-300 text-xs"
                                    data-testid="outbox-ignore-input" />
                                <button type="button" onClick={addIgnore}
                                    className="inline-flex items-center gap-1 text-[11px] font-bold border-2 border-[#111827] text-[#111827] rounded-sm px-2.5 py-1.5 hover:bg-gray-50"
                                    data-testid="outbox-ignore-add">
                                    <Plus className="w-3.5 h-3.5" /> Hinzufügen
                                </button>
                            </div>
                            {ignoreRules.length === 0 ? (
                                <p className="text-xs text-gray-400">Keine Ignorier-Regeln.</p>
                            ) : (
                                <ul className="space-y-1">
                                    {ignoreRules.map((r) => (
                                        <li key={r.id} className="flex items-center gap-2 text-xs border border-gray-200 rounded-sm px-2 py-1" data-testid={`outbox-ignore-rule-${r.id}`}>
                                            <span className="font-mono truncate flex-1">{r.filename}</span>
                                            <button type="button" onClick={() => removeIgnore(r)}
                                                className="p-1 text-gray-400 hover:text-red-600"
                                                data-testid={`outbox-ignore-remove-${r.id}`}>
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                )}
                <DialogFooter>
                    <button type="button" onClick={save} disabled={saving || loading}
                        className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#111827] bg-[#FFCC00] hover:brightness-95 rounded-sm px-4 py-2 disabled:opacity-60"
                        data-testid="outbox-settings-save">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Speichern
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
